import React from 'react';
import { Card, CardContent } from '../../../components/shadcn/ui/card';
import { Badge } from '../../../components/shadcn/ui/badge';
import type { Reason, Risk, Health, Confidence } from './pmApi';
import { sourceKind, safeJiraUrl } from './sourceInfo';

/* Shared presentation for the command center.

   These use the portal's own primitives and Tailwind tokens (muted-foreground,
   border-border, bg-muted) so the pages inherit the admin shell's light/dark
   theme automatically — the app drives dark mode with a `dark` class, so any
   hand-rolled prefers-color-scheme styling would fight it. */

/* Semantic state colours. Kept as a small map rather than scattered ternaries so
   health, confidence, and risk stay visually consistent across all three pages. */
const STATE_STYLES: Record<string, string> = {
  'ON TRACK': 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900',
  WATCH: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900',
  'AT RISK': 'text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-900',
  BLOCKED: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900',
  BREACHED: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900',
  UNLIKELY: 'text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-900',
};

const RISK_STYLES: Record<Risk['level'], string> = {
  Critical: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900',
  High: 'text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-900',
  Moderate: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900',
  Low: 'text-muted-foreground bg-muted border-border',
};

/** Health / confidence. The word carries the meaning; the tint confirms it. */
export function StateLabel({ state, className = '' }: { state: Health | Confidence; className?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${
        STATE_STYLES[state] || 'text-muted-foreground bg-muted border-border'
      } ${className}`}
    >
      {state}
    </span>
  );
}

/** Risk level. Never rendered without its reasons available nearby. */
export function RiskLevel({ level }: { level: Risk['level'] }) {
  return (
    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${RISK_STYLES[level]}`}>
      {level}
    </span>
  );
}

/**
 * Why a thing got its label. This is the component that justifies the risk
 * engine existing: a score nobody can interrogate is not information.
 */
export function ReasonList({ reasons, max, className = '' }: { reasons?: Reason[]; max?: number; className?: string }) {
  if (!reasons?.length) return null;
  const shown = max ? reasons.slice(0, max) : reasons;
  return (
    <ul className={`space-y-1 ${className}`}>
      {shown.map((r, i) => (
        <li key={`${r.code}-${i}`} className="flex gap-2 text-xs leading-relaxed">
          <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
          <span className="min-w-0">
            <span className="font-medium text-foreground/80">{r.label}</span>
            {r.detail ? <span className="text-muted-foreground"> — {r.detail}</span> : null}
          </span>
        </li>
      ))}
      {max && reasons.length > max ? (
        <li className="pl-3 text-xs text-muted-foreground">+{reasons.length - max} more</li>
      ) : null}
    </ul>
  );
}

/** Compact metric tile used across all three pages. Labels wrap, never truncate. */
export function Stat({
  label, value, sub, tone = 'default', onClick,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'default' | 'ok' | 'warn' | 'risk' | 'breach';
  onClick?: () => void;
}) {
  const toneClass = {
    default: 'text-[#242424] dark:text-foreground',
    ok: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    risk: 'text-orange-600 dark:text-orange-400',
    breach: 'text-red-600 dark:text-red-400',
  }[tone];

  const inner = (
    <>
      <p className="text-xs font-medium leading-tight text-muted-foreground">{label}</p>
      <p className={`mt-1.5 text-2xl font-semibold tabular-nums leading-none ${toneClass}`}>{value}</p>
      {sub ? <p className="mt-1.5 text-xs text-muted-foreground">{sub}</p> : null}
    </>
  );

  return (
    <Card className="flex flex-col gap-6 shadow-none border-border">
      <CardContent className={`p-4 ${onClick ? 'cursor-pointer transition-colors hover:bg-muted/40' : ''}`}>
        {onClick ? (
          <button type="button" onClick={onClick} className="w-full text-left">{inner}</button>
        ) : inner}
      </CardContent>
    </Card>
  );
}

export function EmptyState({ icon, title, children }: { icon?: React.ReactNode; title: string; children?: React.ReactNode }) {
  return (
    <div className="px-6 py-10 text-center">
      {icon ? <div className="mx-auto mb-3 text-muted-foreground/60">{icon}</div> : null}
      <p className="text-sm font-medium text-foreground/80">{title}</p>
      {children ? <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">{children}</p> : null}
    </div>
  );
}

export function LoadingBlock({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 px-6 py-12 text-sm text-muted-foreground">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      {label}
    </div>
  );
}

/** Marks where a value came from — source import vs entered by leadership. */
export function OriginBadge({ origin }: { origin: string }) {
  const label = origin === 'human_entered' ? 'Entered here' : origin === 'system_derived' ? 'Derived' : 'From plan';
  return <Badge variant="outline" className="text-[10px] font-normal">{label}</Badge>;
}

/**
 * Where an item lives. A Jira item says "Jira" and links its key to the issue
 * (a new tab, never carrying this page as referrer); anything else falls back
 * to the origin badge it always had.
 */
export function SourceBadge({
  externalSource, sourceKey, sourceUrl,
}: {
  externalSource: string;
  sourceKey?: string | null;
  sourceUrl?: string | null;
}) {
  if (sourceKind(externalSource) !== 'jira') {
    return <OriginBadge origin={externalSource === 'manual' ? 'human_entered' : 'source'} />;
  }
  const href = safeJiraUrl(sourceUrl);
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant="outline" className="border-blue-200 text-[10px] font-medium text-blue-700 dark:border-blue-900 dark:text-blue-400">
        Jira
      </Badge>
      {sourceKey ? (
        href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-medium tabular-nums text-blue-700 underline-offset-2 hover:underline dark:text-blue-400"
            aria-label={`Open ${sourceKey} in Jira (new tab)`}
          >
            {sourceKey}
          </a>
        ) : (
          <span className="text-[11px] tabular-nums text-muted-foreground">{sourceKey}</span>
        )
      ) : null}
    </span>
  );
}

/* ── Formatting ─────────────────────────────────────────────────────────── */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : '—';
}

export function fmtDateFull(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : '—';
}

export function fmtWhen(ts: string): string {
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days < 30 ? `${days}d ago` : fmtDateFull(new Date(ts).toISOString());
}

/**
 * Strips the plan's leading emoji markers (🎯 ⚠️ 🔴 ⏸️) for dense UI.
 * Avoids \p{...} and the /u flag on purpose: this app targets ES5, where both
 * are compile errors. Latin-Extended is preserved so accented initials survive.
 */
export function cleanName(name: string): string {
  return String(name || '').replace(/^[^A-Za-z0-9À-ɏ]+/, '').trim();
}

export function cleanBlocker(text: string): string {
  return String(text || '').replace(/^⏸️?\s*/, '').trim();
}
